// Client-side semantic search.
//
// The query is embedded in the browser with Transformers.js (loaded from a CDN,
// so it never enters the webpack bundle) using the same model that produced the
// build-time vectors in public/assets/embeddings.json. Ranking is a plain cosine
// dot product over those vectors. No API key, no backend, no per-query cost.

const MODEL = 'Xenova/all-MiniLM-L6-v2';

// Minimum cosine similarity for a result to show in search. Calibrated on a probe
// set: clear off-topic <= ~0.22, genuine matches mostly >= ~0.45, rare true
// matches dip to ~0.26. 0.24 favours recall. (MiniLM's wide score spread is what
// makes this work — e5 compresses scores and an absolute floor fails there.)
const SCORE_FLOOR = 0.24;

let extractorPromise = null;
let dataPromise = null;

// Load Transformers.js from the CDN as a native ES module. webpackIgnore keeps
// the bundler from trying to resolve the URL, so the heavy library and its wasm
// stay out of the site bundle and load only when semantic search is first used.
async function getExtractor() {
  if (!extractorPromise) {
    extractorPromise = (async () => {
      const t = await import(
        /* webpackIgnore: true */ 'https://cdn.jsdelivr.net/npm/@xenova/transformers@2.17.2'
      );
      t.env.allowLocalModels = false;
      t.env.useBrowserCache = true;
      return t.pipeline('feature-extraction', MODEL);
    })();
    // Don't cache a failure: a flaky first load should not disable semantic
    // search until the next full page reload.
    extractorPromise.catch(() => { extractorPromise = null; });
  }
  return extractorPromise;
}

// Load + dequantize the baked vectors once (int8 -> Float32Array for fast dot).
async function getData() {
  if (!dataPromise) {
    dataPromise = fetch('/assets/embeddings.json', { cache: 'no-cache' })
      .then((r) => {
        if (!r.ok) throw new Error(`embeddings.json ${r.status}`);
        return r.json();
      })
      .then((d) => {
        d.vecs = d.items.map((it) => {
          const f = new Float32Array(d.dim);
          const s = it.scale / 127;
          for (let i = 0; i < d.dim; i++) f[i] = it.q[i] * s;
          return { id: it.id, f };
        });
        return d;
      });
    dataPromise.catch(() => { dataPromise = null; });
  }
  return dataPromise;
}

// Warm the model and data together; resolves once ranking is possible.
export function ensureReady() {
  return Promise.all([getExtractor(), getData()]);
}

// All chunks ranked by cosine similarity to the query. Returns [{ id, score }].
async function rankAll(query) {
  const [extractor, data] = await Promise.all([getExtractor(), getData()]);
  const out = await extractor(query, { pooling: 'mean', normalize: true });
  const q = out.data;
  const scored = data.vecs.map((v) => {
    let dot = 0;
    for (let i = 0; i < data.dim; i++) dot += q[i] * v.f[i];
    return { id: v.id, score: dot };
  });
  return scored.sort((a, b) => b.score - a.score);
}

// For search: drop weak matches so off-topic queries surface "no matches"
// rather than a padded-out top-N of unrelated chunks.
export async function semanticRank(query, topk = 12) {
  return (await rankAll(query)).filter((x) => x.score >= SCORE_FLOOR).slice(0, topk);
}

// For Ask/RAG retrieval: raw top-k regardless of floor. We want the best
// available context even when scores are low; the model decides if it can answer.
export async function semanticTopK(query, topk = 5) {
  return (await rankAll(query)).slice(0, topk);
}

// Every chunk's score, best first, no floor. The search-story page uses this to
// draw the full score distribution behind the threshold demo.
export async function semanticScores(query) {
  return rankAll(query);
}

export { SCORE_FLOOR };
